import * as Sentry from "@sentry/react";
import { SupabaseClient } from "@supabase/supabase-js";
import { supabaseIntegration } from "@supabase/sentry-js-integration";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { useEffect } from "react";

// Sentry configuration
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

export function initSentry() {
  // Only initialize Sentry if DSN is provided
  if (!SENTRY_DSN) {
    console.warn("Sentry DSN not found. Skipping Sentry initialization.");
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    integrations: [
      // Supabase integration for monitoring Supabase client operations
      supabaseIntegration(SupabaseClient, Sentry, {
        tracing: true,
        breadcrumbs: true,
        errors: true,
      }),
      // React Router v6 integration
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      // Browser tracing with Supabase URL filtering to avoid duplicates
      Sentry.browserTracingIntegration({
        shouldCreateSpanForRequest: (url) => {
          return !url.startsWith(`${SUPABASE_URL}/rest`);
        },
      }),
      // Session replay for better debugging (optional)
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Performance monitoring - adjust in production
    tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.1,

    // Session replay sampling
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Add user context and request headers
    sendDefaultPii: true,

    // Enable debug mode in development
    debug: import.meta.env.DEV,

    // Set environment
    environment: import.meta.env.MODE,

    // Add release information if available
    release: import.meta.env.VITE_APP_VERSION || "development",

    // Before sending errors
    beforeSend(event, hint) {
      // Filter out certain errors if needed
      if (event.exception) {
        const error = hint.originalException;
        // Example: Ignore ResizeObserver errors
        if (
          error &&
          error instanceof Error &&
          error.message.includes("ResizeObserver")
        ) {
          return null;
        }
      }
      return event;
    },
  });

  // Set additional context
  Sentry.setTag("app", "firmagent-frontend");
  Sentry.setContext("app_info", {
    mode: import.meta.env.MODE,
    dev: import.meta.env.DEV,
  });
}

// Helper function to capture exceptions
export function captureException(error: Error, context?: Record<string, any>) {
  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value);
      });
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
}

// Helper function to capture messages
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = "info",
  context?: Record<string, any>,
) {
  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value);
      });
      Sentry.captureMessage(message, level);
    });
  } else {
    Sentry.captureMessage(message, level);
  }
}

// Helper function to set user context
export function setUser(user: {
  id: string;
  email?: string;
  username?: string;
}) {
  Sentry.setUser(user);
}

// Helper function to add breadcrumbs
export function addBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, any>,
) {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: "info",
    timestamp: Date.now() / 1000,
  });
}
